import { ASSISTANT_GROUP_TEMPLATES } from '@/interfaces/assistant.interface';
import type {
  IAssistantOpDto,
  IChoiceInputDto,
  IHourRangeDto,
  IPriceEntryDto,
  IRecipeLineDto,
  ISizeInputDto,
  IStockQuantityDto,
  TAssistantOpName,
  TDiscountMode,
  TGroupTemplate,
  TPlacement,
  TStockUnit,
} from '@/interfaces/assistant.interface';
import { HostToolError } from './tool-types';
import {
  getOptionalBoolean,
  getOptionalEnum,
  getOptionalNumber,
  getOptionalNumberArray,
  getOptionalString,
  isRecord,
} from './tool-args';

export const BRANCH_SCOPES = ['current', 'all'] as const;

/** What a price change does to a discounted price (plan P0-2, D2). */
export const DISCOUNT_MODES: readonly TDiscountMode[] = [
  'keep',
  'remove',
  'scale',
];

/** Where a reorder puts the listed ids (plan P2-1; the API's default is 'first'). */
export const PLACEMENTS: readonly TPlacement[] = ['first', 'last'];

const isGroupTemplate = (key: string): key is TGroupTemplate =>
  Object.prototype.hasOwnProperty.call(ASSISTANT_GROUP_TEMPLATES, key);

/** The rules a new option group starts from (plan P3-0, the Opciones editor's templates). */
export const GROUP_TEMPLATES: readonly TGroupTemplate[] = Object.keys(
  ASSISTANT_GROUP_TEMPLATES
).filter(isGroupTemplate);

/**
 * A ref the model gives something a call creates, so other changes of the
 * call can point at it (plan F2.7, the Action API's own pattern).
 */
export const REF_PATTERN = /^[a-z0-9_-]{1,16}$/;

/** The rounding steps the Action API knows (each currency allows some of them). */
export const ROUND_STEPS = [0.05, 0.1, 0.5, 1, 5, 10, 50, 100, 500, 1000];

/** A time of a schedule row, `HH:MM` from 00:00 to 23:59 (plan P4-5). */
export const HOUR_PATTERN = /^([01]\d|2[0-3]):[0-5]\d$/;

/** The days of a schedule row: 0 = Sunday … 6 = Saturday (plan P4-2). */
const SCHEDULE_DAY_MAX = 6;

/** What a stock movement records (plan P5-1): bought, lost, or the counted total. */
export const STOCK_MOVEMENTS: ReadonlyArray<
  NonNullable<IAssistantOpDto['movement']>
> = ['purchase', 'loss', 'count'];

/** The units of a stock item (plan P5-1, the admin's `StockUnit`). */
export const STOCK_UNITS: readonly TStockUnit[] = [
  'g',
  'kg',
  'ml',
  'l',
  'un',
  'pcs',
];

/** What a stock item is (plan P5-1; 'ingredient' when absent). */
export const STOCK_TYPES: ReadonlyArray<
  NonNullable<IAssistantOpDto['stockType']>
> = ['ingredient', 'product'];

/** The decimals a stock quantity may have (the Action API's `maxDecimalPlaces`, plan P5-0). */
const STOCK_DECIMALS = 3;

/** Plan §3.8 ranges of `IAssistantOpDto`. */
export const MENU_OP_LIMITS = {
  changes: 20,
  /** `createItem` changes in one proposal (the API answers more with 422). */
  creates: 10,
  itemIds: 500,
  categoryIds: 100,
  price: 99999999.99,
  percentMin: -90,
  percentMax: 500,
  amount: 99999999.99,
  name: 128,
  description: 1000,
  /** `prices` entries in one change (the API's row limit). */
  prices: 500,
  /** Option groups (`optionGroupIds`) and choices (`choiceIds`) one change lists. */
  groupIds: 100,
  choiceIds: 100,
  /** `choices` of one change (the API's `MAX_GROUP_CHOICES`, plan P3-1). */
  choices: 50,
  /** The highest `min` / `max` of an option group's rules. */
  ruleMax: 100,
  /** A product's sizes (`sizes`, `sizeIds`): the API's `MAX_SIZES` (plan P4-1). */
  sizes: 20,
  /** Rows of one schedule (`hourRanges`): the API's `MAX_HOUR_RANGES` (plan P4-2). */
  hourRanges: 50,
  /** A product's preparation notes (plan P4-4). */
  notes: 2000,
  /** Stock items of one recordStock (`items`, plan P5-1). */
  stockItems: 100,
  /** A stock movement's note (plan P5-1). */
  stockNote: 200,
  /** Lines of one recipe (`lines`, plan P5-1). */
  recipeLines: 30,
  /**
   * The largest stock quantity of any kind: a movement, a total, a minimum,
   * a recipe line (the Action API's `@Max`, plan P5-0, W22 F5).
   */
  stockQuantity: 999999999.999,
};

/** A field of a change: every key of the op DTO but `op`. */
export type TOpField = Exclude<keyof IAssistantOpDto, 'op'>;

/** The JSON Schema of one field, as the tool definition carries it. */
export type TFieldSchema = Readonly<Record<string, unknown>>;

/**
 * discountMode's text for what takes it (plan P0-2; F10: it names no op).
 * `targets` = what can have a discounted price whose regular price changes.
 */
const discountModeText = (targets: string): string =>
  `Only after asking the user, for ${targets} that has a discounted price: 'keep' = only the regular price changes and customers keep paying the discounted price; 'remove' = the discount ends and customers pay the new price; 'scale' (percent or amount changes only) = the discounted prices move by the same percent or amount. Never choose for the user.`;

/**
 * The schema of each field (plan F10), in the order the tool definition
 * lists them, which is also the order the fields are checked in. Field texts
 * name no op or tool: the `op` lines say which fields each op takes.
 */
export const FIELD_SCHEMAS: Readonly<Record<TOpField, TFieldSchema>> = {
  itemIds: {
    type: 'array',
    items: { type: 'integer' },
    maxItems: MENU_OP_LIMITS.itemIds,
  },
  categoryIds: {
    type: 'array',
    items: { type: 'integer' },
    maxItems: MENU_OP_LIMITS.categoryIds,
  },
  all: {
    type: 'boolean',
    description: 'true for the whole menu.',
  },
  itemId: { type: 'integer' },
  sizeId: { type: 'integer' },
  categoryId: { type: 'integer' },
  available: { type: 'boolean' },
  branchScope: { type: 'string', enum: [...BRANCH_SCOPES] },
  price: {
    type: 'number',
    minimum: 0,
    maximum: MENU_OP_LIMITS.price,
  },
  percent: {
    type: 'number',
    minimum: MENU_OP_LIMITS.percentMin,
    maximum: MENU_OP_LIMITS.percentMax,
  },
  amount: {
    type: 'number',
    minimum: -MENU_OP_LIMITS.amount,
    maximum: MENU_OP_LIMITS.amount,
  },
  roundTo: { type: 'number', enum: [...ROUND_STEPS] },
  name: {
    type: 'string',
    minLength: 1,
    maxLength: MENU_OP_LIMITS.name,
  },
  description: {
    type: 'string',
    maxLength: MENU_OP_LIMITS.description,
  },
  discountMode: {
    type: 'string',
    enum: [...DISCOUNT_MODES],
    description: discountModeText('a product or size'),
  },
  discountedPrice: {
    type: ['number', 'null'],
    minimum: 0,
    maximum: MENU_OP_LIMITS.price,
    description: 'The discounted price customers pay (null removes it).',
  },
  prices: {
    type: 'array',
    maxItems: MENU_OP_LIMITS.prices,
    items: {
      type: 'object',
      additionalProperties: false,
      required: ['itemId'],
      properties: {
        itemId: { type: 'integer' },
        sizeId: { type: 'integer' },
        price: { type: 'number', minimum: 0, maximum: MENU_OP_LIMITS.price },
        discountedPrice: {
          type: ['number', 'null'],
          minimum: 0,
          maximum: MENU_OP_LIMITS.price,
        },
      },
    },
  },
  ref: {
    type: 'string',
    pattern: REF_PATTERN.source,
    description:
      'Your short id (a-z, 0-9, - or _, ≤16) for something this call creates, so other changes of this call can point at it.',
  },
  categoryRef: {
    type: 'string',
    pattern: REF_PATTERN.source,
    description: 'The ref of a category created in this same call.',
  },
  menuId: {
    type: ['integer', 'null'],
    description: 'The menu (stores with several menus).',
  },
  posOnly: { type: 'boolean' },
  placement: { type: 'string', enum: [...PLACEMENTS] },
  optionGroupId: { type: 'integer' },
  optionGroupIds: {
    type: 'array',
    items: { type: 'integer' },
    maxItems: MENU_OP_LIMITS.groupIds,
  },
  choiceId: { type: 'integer' },
  choiceIds: {
    type: 'array',
    items: { type: 'integer' },
    maxItems: MENU_OP_LIMITS.choiceIds,
  },
  template: { type: 'string', enum: [...GROUP_TEMPLATES] },
  required: { type: 'boolean' },
  multiple: { type: 'boolean' },
  min: {
    type: ['integer', 'null'],
    minimum: 0,
    maximum: MENU_OP_LIMITS.ruleMax,
  },
  max: {
    type: ['integer', 'null'],
    minimum: 0,
    maximum: MENU_OP_LIMITS.ruleMax,
  },
  choices: {
    type: 'array',
    maxItems: MENU_OP_LIMITS.choices,
    items: {
      type: 'object',
      additionalProperties: false,
      required: ['name'],
      properties: {
        name: {
          type: 'string',
          minLength: 1,
          maxLength: MENU_OP_LIMITS.name,
        },
        price: { type: 'number', minimum: 0, maximum: MENU_OP_LIMITS.price },
        discountedPrice: {
          type: ['number', 'null'],
          minimum: 0,
          maximum: MENU_OP_LIMITS.price,
        },
        description: {
          type: 'string',
          maxLength: MENU_OP_LIMITS.description,
        },
        available: { type: 'boolean' },
      },
    },
  },
  copyOf: { type: 'integer', description: 'The id of the group to copy.' },
  sizeIds: {
    type: 'array',
    items: { type: 'integer' },
    maxItems: MENU_OP_LIMITS.sizes,
  },
  sizes: {
    type: 'array',
    maxItems: MENU_OP_LIMITS.sizes,
    items: {
      type: 'object',
      additionalProperties: false,
      required: ['name', 'price'],
      properties: {
        name: {
          type: 'string',
          minLength: 1,
          maxLength: MENU_OP_LIMITS.name,
        },
        price: { type: 'number', minimum: 0, maximum: MENU_OP_LIMITS.price },
        discountedPrice: {
          type: ['number', 'null'],
          minimum: 0,
          maximum: MENU_OP_LIMITS.price,
        },
      },
    },
  },
  hourRanges: {
    type: ['array', 'null'],
    maxItems: MENU_OP_LIMITS.hourRanges,
    items: {
      type: 'object',
      additionalProperties: false,
      required: ['day', 'from', 'to'],
      properties: {
        day: { type: 'integer', minimum: 0, maximum: SCHEDULE_DAY_MAX },
        from: { type: 'string', pattern: HOUR_PATTERN.source },
        to: { type: 'string', pattern: HOUR_PATTERN.source },
      },
    },
  },
  notes: { type: 'string', maxLength: MENU_OP_LIMITS.notes },
  stockItemId: { type: 'integer' },
  movement: { type: 'string', enum: [...STOCK_MOVEMENTS] },
  items: {
    type: 'array',
    maxItems: MENU_OP_LIMITS.stockItems,
    items: {
      type: 'object',
      additionalProperties: false,
      required: ['stockItemId', 'quantity'],
      properties: {
        stockItemId: { type: 'integer' },
        quantity: {
          type: 'number',
          minimum: 0,
          maximum: MENU_OP_LIMITS.stockQuantity,
        },
      },
    },
  },
  note: { type: 'string', maxLength: MENU_OP_LIMITS.stockNote },
  unit: { type: 'string', enum: [...STOCK_UNITS] },
  stockType: { type: 'string', enum: [...STOCK_TYPES] },
  cost: { type: 'number', minimum: 0, maximum: MENU_OP_LIMITS.price },
  minimumQuantity: {
    type: 'number',
    minimum: 0,
    maximum: MENU_OP_LIMITS.stockQuantity,
  },
  quantity: {
    type: 'number',
    minimum: 0,
    maximum: MENU_OP_LIMITS.stockQuantity,
  },
  lines: {
    type: 'array',
    maxItems: MENU_OP_LIMITS.recipeLines,
    items: {
      type: 'object',
      additionalProperties: false,
      required: ['quantity'],
      properties: {
        stockItemId: { type: 'integer' },
        stockItemRef: {
          type: 'string',
          pattern: REF_PATTERN.source,
          description: 'The ref of a stock item created in this same call.',
        },
        quantity: {
          type: 'number',
          exclusiveMinimum: 0,
          maximum: MENU_OP_LIMITS.stockQuantity,
        },
      },
    },
  },
};

const isOpField = (key: string): key is TOpField =>
  Object.prototype.hasOwnProperty.call(FIELD_SCHEMAS, key);

/** Every field, in `FIELD_SCHEMAS` order. */
const FIELDS: readonly TOpField[] = Object.keys(FIELD_SCHEMAS).filter(
  isOpField
);

/**
 * A field's schema while `op` is offered, in place of its `FIELD_SCHEMAS`
 * one (plan F10): discountMode names option choices only while
 * updateOptionChoice, which takes it for a choice, is offered (plan P3-5),
 * so a member without the Phase 3 ops keeps the earlier text.
 */
const SCHEMAS_WITH_OP: Readonly<
  Partial<Record<TOpField, { op: TAssistantOpName; schema: TFieldSchema }>>
> = {
  discountMode: {
    op: 'updateOptionChoice',
    schema: {
      ...FIELD_SCHEMAS.discountMode,
      description: discountModeText('a product, size or option choice'),
    },
  },
};

/** The schema of `field` in a definition that offers `ops`. */
function schemaOf(
  field: TOpField,
  ops: readonly TAssistantOpName[]
): TFieldSchema {
  const variant = SCHEMAS_WITH_OP[field];
  return variant !== undefined && ops.includes(variant.op)
    ? variant.schema
    : FIELD_SCHEMAS[field];
}

/**
 * The fields each op takes (plan F10): a tool's schema offers the fields of
 * its offered ops, and a change keeps only its own op's fields (the Action
 * API drops the rest too). Which of them an op requires stays the API's rule
 * (422 back to the model). An op with a target takes all three target fields:
 * exactly one of them, and `all` only where allowed, is the API's rule too.
 */
export const OP_FIELDS: Readonly<
  Record<TAssistantOpName, readonly TOpField[]>
> = {
  setAvailability: [
    'itemIds',
    'categoryIds',
    'all',
    'available',
    'branchScope',
  ],
  setPrice: ['itemId', 'sizeId', 'price', 'discountMode'],
  adjustPrices: [
    'itemIds',
    'categoryIds',
    'all',
    'percent',
    'amount',
    'roundTo',
    'discountMode',
  ],
  renameItem: ['itemId', 'name'],
  setDescription: ['itemId', 'description'],
  // `available: false` = created hidden, which needs every branch (plan
  // P2-1; the admin's lock reads it too, plan F10).
  createItem: [
    'categoryId',
    'categoryRef',
    'name',
    'price',
    'description',
    'discountedPrice',
    'available',
  ],
  setDiscount: ['itemId', 'sizeId', 'discountedPrice'],
  setPrices: ['prices', 'discountMode'],
  createCategory: ['ref', 'name', 'description', 'menuId', 'available'],
  renameCategory: ['categoryId', 'name'],
  setCategoryDescription: ['categoryId', 'description'],
  setCategoryAvailability: ['categoryIds', 'available', 'branchScope'],
  setCategoryPosOnly: ['categoryIds', 'posOnly'],
  moveItems: ['itemIds', 'categoryId', 'categoryRef'],
  reorderItems: ['categoryId', 'itemIds', 'placement'],
  reorderCategories: ['categoryIds', 'menuId', 'placement'],
  // Without `copyOf`, name, template and choices are required (the API's
  // rule, plan P3-1).
  createOptionGroup: [
    'name',
    'template',
    'choices',
    'itemIds',
    'required',
    'min',
    'max',
    'description',
    'placement',
    'copyOf',
  ],
  updateOptionGroup: [
    'optionGroupId',
    'name',
    'description',
    'required',
    'multiple',
    'min',
    'max',
  ],
  linkOptionGroup: ['optionGroupId', 'itemIds', 'placement'],
  unlinkOptionGroup: ['optionGroupId', 'itemIds'],
  reorderOptionGroups: ['itemId', 'optionGroupIds', 'placement'],
  addOptionChoices: ['optionGroupId', 'choices'],
  updateOptionChoice: [
    'optionGroupId',
    'choiceId',
    'name',
    'description',
    'price',
    'discountedPrice',
    'discountMode',
    'available',
  ],
  removeOptionChoices: ['optionGroupId', 'choiceIds'],
  reorderOptionChoices: ['optionGroupId', 'choiceIds', 'placement'],
  // A product's sizes (plan P4-1): their prices change through setPrice,
  // setDiscount and setPrices with `sizeId`. `discountMode` decides the
  // discount of a product that switches to sizes or back to one price.
  addSizes: ['itemId', 'sizes', 'discountMode'],
  updateSize: ['itemId', 'sizeId', 'name', 'available'],
  removeSizes: [
    'itemId',
    'sizeIds',
    'price',
    'discountedPrice',
    'discountMode',
  ],
  reorderSizes: ['itemId', 'sizeIds', 'placement'],
  // null or [] = always (plan P4-2).
  setItemSchedule: ['itemIds', 'hourRanges'],
  setCategorySchedule: ['categoryIds', 'hourRanges'],
  duplicateItem: ['itemId', 'name'],
  setPreparationNotes: ['itemId', 'notes'],
  // Inventory (plan P5-1): purchase/loss = the amount moved, count = the
  // counted total.
  recordStock: ['movement', 'items', 'note'],
  // `ref`: other changes of the call point at it as `stockItemRef`.
  createStockItem: [
    'ref',
    'name',
    'unit',
    'stockType',
    'cost',
    'minimumQuantity',
    'quantity',
  ],
  updateStockItem: ['stockItemId', 'name', 'unit', 'cost', 'minimumQuantity'],
  // The recipe of a product (`itemId`), a size (+ `sizeId`) or an option
  // choice (`optionGroupId` + `choiceId`); `lines: []` removes it.
  setRecipe: ['itemId', 'sizeId', 'optionGroupId', 'choiceId', 'lines'],
};

/** The fields `ops` take, in `FIELD_SCHEMAS` order. */
function fieldsOf(ops: readonly TAssistantOpName[]): TOpField[] {
  return FIELDS.filter((field) =>
    ops.some((op) => OP_FIELDS[op].includes(field))
  );
}

/**
 * Fields an op takes only while another op is offered, and that op: a
 * category ref points at what createCategory makes (plan F2.7: no ref can
 * exist without it), and createItem's Phase 2 fields come with the Phase 2
 * ops (W13 decision: their dark launch, or createCategory switched off,
 * leaves createItem as Phase 1 offered it).
 */
const OFFERED_ONLY_WITH: Readonly<
  Partial<
    Record<
      TAssistantOpName,
      Readonly<Partial<Record<TOpField, TAssistantOpName>>>
    >
  >
> = {
  createItem: {
    categoryRef: 'createCategory',
    discountedPrice: 'createCategory',
    available: 'createCategory',
  },
  moveItems: { categoryRef: 'createCategory' },
};

/** The fields `op` takes in a schema that offers `ops`, in `OP_FIELDS` order. */
function offeredFieldsOf(
  op: TAssistantOpName,
  ops: readonly TAssistantOpName[]
): TOpField[] {
  const gated = OFFERED_ONLY_WITH[op] ?? {};
  return OP_FIELDS[op].filter((field) => {
    const source = gated[field];
    return source === undefined || ops.includes(source);
  });
}

/**
 * The schemas of the fields `ops` take, in `FIELD_SCHEMAS` order (plan F10):
 * the item properties of a proposal tool that offers those ops. A field an
 * op takes only with another op comes only with that op (unless another op
 * takes it anyway); a change that sends it without that op is refused
 * (`opsFromArgs`). A field whose text depends on the ops offered takes the
 * text of those ops (`SCHEMAS_WITH_OP`).
 */
export function fieldSchemasOf(
  ops: readonly TAssistantOpName[]
): Record<string, TFieldSchema> {
  const offered = new Set(ops.flatMap((op) => offeredFieldsOf(op, ops)));
  const schemas: Record<string, TFieldSchema> = {};
  for (const field of FIELDS)
    if (offered.has(field)) schemas[field] = schemaOf(field, ops);
  return schemas;
}

function invalid(
  key: string,
  value: unknown,
  extra: Record<string, unknown> = {}
) {
  return new HostToolError('INVALID_ARGS', `Invalid "${key}"`, {
    key,
    value,
    ...extra,
  });
}

/** A number within [min, max]; undefined when absent. */
function numberIn(
  raw: Record<string, unknown>,
  key: string,
  min: number,
  max: number
): number | undefined {
  const value = getOptionalNumber(raw, key);
  if (value === null) return undefined;
  if (!Number.isFinite(value) || value < min || value > max)
    throw invalid(key, value, { min, max });
  return value;
}

/** A positive integer id; undefined when absent. */
function idOf(raw: Record<string, unknown>, key: string): number | undefined {
  const value = getOptionalNumber(raw, key);
  if (value === null) return undefined;
  if (!Number.isInteger(value) || value <= 0) throw invalid(key, value);
  return value;
}

/** Positive integer ids, without repeats; undefined when absent or empty. */
function idsOf(
  raw: Record<string, unknown>,
  key: string,
  max: number
): number[] | undefined {
  const value = raw[key];
  if (Array.isArray(value) && value.length === 0) return undefined;
  const ids = getOptionalNumberArray(raw, key, max);
  if (ids === null) return undefined;
  if (ids.some((id) => !Number.isInteger(id) || id <= 0))
    throw invalid(key, value);
  return Array.from(new Set(ids));
}

/**
 * A product's size ids (plan P4-1), read like `idsOf` except that a sent
 * empty list is kept: removeSizes without `sizeIds` removes every size (back
 * to one price), so an empty list must never read as absent; the Action API
 * refuses it (MISSING_FIELD `sizeIds`). An op that does not take `sizeIds`
 * drops it.
 */
function sizeIdsOf(
  raw: Record<string, unknown>,
  key: string
): number[] | undefined {
  const value = raw[key];
  if (Array.isArray(value) && value.length === 0) return [];
  return idsOf(raw, key, MENU_OP_LIMITS.sizes);
}

/** A string of at most `max` characters (at least `min`); undefined when absent. */
function textOf(
  raw: Record<string, unknown>,
  key: string,
  min: number,
  max: number
): string | undefined {
  const value = getOptionalString(raw, key);
  if (value === null) return undefined;
  const length = Array.from(value).length;
  if (length < min || length > max) throw invalid(key, value, { min, max });
  return value;
}

/** One of the rounding steps; undefined when absent. */
function roundStepOf(
  raw: Record<string, unknown>,
  key: string
): number | undefined {
  const value = getOptionalNumber(raw, key);
  if (value === null) return undefined;
  if (!ROUND_STEPS.includes(value))
    throw invalid(key, value, { allowed: ROUND_STEPS });
  return value;
}

/**
 * A ref (plan F2.7: a-z, 0-9, - or _, at most 16); undefined when absent or
 * empty. Whether it names a category of the call is the API's rule.
 */
function refOf(raw: Record<string, unknown>, key: string): string | undefined {
  const value = getOptionalString(raw, key);
  if (value === null || value === '') return undefined;
  if (!REF_PATTERN.test(value))
    throw invalid(key, value, { pattern: REF_PATTERN.source });
  return value;
}

/**
 * A menu id that may be null: null is the base menu, so it is kept;
 * undefined when absent.
 */
function nullableIdOf(
  raw: Record<string, unknown>,
  key: string
): number | null | undefined {
  if (raw[key] === null) return null;
  return idOf(raw, key);
}

/**
 * A price that may be null: null removes a discounted price, so it is kept;
 * undefined when absent.
 */
function nullableMoneyOf(
  raw: Record<string, unknown>,
  key: string
): number | null | undefined {
  if (raw[key] === null) return null;
  return numberIn(raw, key, 0, MENU_OP_LIMITS.price);
}

/**
 * A rule count of an option group that may be null: null is no limit, so it
 * is kept; undefined when absent. A whole number within [0, 100].
 */
function nullableCountOf(
  raw: Record<string, unknown>,
  key: string
): number | null | undefined {
  if (raw[key] === null) return null;
  const value = numberIn(raw, key, 0, MENU_OP_LIMITS.ruleMax);
  if (value !== undefined && !Number.isInteger(value))
    throw invalid(key, value, { min: 0, max: MENU_OP_LIMITS.ruleMax });
  return value;
}

/**
 * Up to `max` entries of a nested list, each read by `read` (an error names
 * the entry's field, e.g. «prices[3].price»); undefined when absent or empty.
 */
function entriesOf<T>(
  raw: Record<string, unknown>,
  key: string,
  max: number,
  read: (entry: Record<string, unknown>) => T
): T[] | undefined {
  const value = raw[key];
  if (value === undefined || value === null) return undefined;
  if (!Array.isArray(value) || value.length > max)
    throw invalid(key, value, { max });
  if (value.length === 0) return undefined;
  return value.map((entry: unknown, index) => {
    const path = `${key}[${index}]`;
    if (!isRecord(entry)) throw invalid(path, entry);
    try {
      return read(entry);
    } catch (error) {
      if (!(error instanceof HostToolError) || !isRecord(error.details))
        throw error;
      const field = `${path}.${String(error.details.key)}`;
      throw new HostToolError(error.code, `Invalid "${field}"`, {
        ...error.details,
        key: field,
      });
    }
  });
}

/** The fields of one `prices` entry. */
const PRICE_ENTRY_FIELDS: ReadonlyArray<keyof IPriceEntryDto> = [
  'itemId',
  'sizeId',
  'price',
  'discountedPrice',
];

/** One `prices` entry: its product is required, the rest kept when sent. */
function priceEntryOf(raw: Record<string, unknown>): IPriceEntryDto {
  const unknown = Object.keys(raw).find(
    (key) => !PRICE_ENTRY_FIELDS.some((field) => field === key)
  );
  if (unknown !== undefined) throw invalid(unknown, raw[unknown]);
  const itemId = idOf(raw, 'itemId');
  if (itemId === undefined) throw invalid('itemId', raw.itemId);
  const entry: IPriceEntryDto = { itemId };
  const sizeId = idOf(raw, 'sizeId');
  if (sizeId !== undefined) entry.sizeId = sizeId;
  const price = numberIn(raw, 'price', 0, MENU_OP_LIMITS.price);
  if (price !== undefined) entry.price = price;
  const discountedPrice = nullableMoneyOf(raw, 'discountedPrice');
  if (discountedPrice !== undefined) entry.discountedPrice = discountedPrice;
  return entry;
}

/**
 * Up to 500 price entries, each checked (an error names the entry's field,
 * e.g. «prices[3].price»); undefined when absent or empty. Which of `price`
 * and `discountedPrice` an entry needs, and a product named twice, are the
 * API's rule.
 */
function priceEntriesOf(
  raw: Record<string, unknown>,
  key: string
): IPriceEntryDto[] | undefined {
  return entriesOf(raw, key, MENU_OP_LIMITS.prices, priceEntryOf);
}

/** The fields of one option choice (plan P3-1). */
const CHOICE_FIELDS: ReadonlyArray<keyof IChoiceInputDto> = [
  'name',
  'price',
  'discountedPrice',
  'description',
  'available',
];

/**
 * One option choice: its name is required, the rest kept when sent
 * (`available: false` adds it paused).
 */
function choiceOf(raw: Record<string, unknown>): IChoiceInputDto {
  const unknown = Object.keys(raw).find(
    (key) => !CHOICE_FIELDS.some((field) => field === key)
  );
  if (unknown !== undefined) throw invalid(unknown, raw[unknown]);
  const name = textOf(raw, 'name', 1, MENU_OP_LIMITS.name);
  if (name === undefined) throw invalid('name', raw.name);
  const choice: IChoiceInputDto = { name };
  const price = numberIn(raw, 'price', 0, MENU_OP_LIMITS.price);
  if (price !== undefined) choice.price = price;
  const discountedPrice = nullableMoneyOf(raw, 'discountedPrice');
  if (discountedPrice !== undefined) choice.discountedPrice = discountedPrice;
  const description = textOf(raw, 'description', 0, MENU_OP_LIMITS.description);
  if (description !== undefined) choice.description = description;
  const available = getOptionalBoolean(raw, 'available');
  if (available !== null) choice.available = available;
  return choice;
}

/**
 * Up to 50 option choices, each checked (an error names the choice's field,
 * e.g. «choices[2].price»); undefined when absent or empty. Whether a
 * discounted price stays below its price is the API's rule.
 */
function choicesOf(
  raw: Record<string, unknown>,
  key: string
): IChoiceInputDto[] | undefined {
  return entriesOf(raw, key, MENU_OP_LIMITS.choices, choiceOf);
}

/** The fields of one size of addSizes (plan P4-1). */
const SIZE_FIELDS: ReadonlyArray<keyof ISizeInputDto> = [
  'name',
  'price',
  'discountedPrice',
];

/**
 * One size of a product: its name and price are required; its discounted
 * price is kept when sent, null included (on the first size it decides the
 * discount of a product that switches to sizes, plan P4-1).
 */
function sizeOf(raw: Record<string, unknown>): ISizeInputDto {
  const unknown = Object.keys(raw).find(
    (key) => !SIZE_FIELDS.some((field) => field === key)
  );
  if (unknown !== undefined) throw invalid(unknown, raw[unknown]);
  const name = textOf(raw, 'name', 1, MENU_OP_LIMITS.name);
  if (name === undefined) throw invalid('name', raw.name);
  const price = numberIn(raw, 'price', 0, MENU_OP_LIMITS.price);
  if (price === undefined) throw invalid('price', raw.price);
  const size: ISizeInputDto = { name, price };
  const discountedPrice = nullableMoneyOf(raw, 'discountedPrice');
  if (discountedPrice !== undefined) size.discountedPrice = discountedPrice;
  return size;
}

/**
 * Up to 20 sizes, each checked (an error names the size's field, e.g.
 * «sizes[1].price»); undefined when absent or empty. Unique names and a
 * discounted price below its price are the API's rule.
 */
function sizesOf(
  raw: Record<string, unknown>,
  key: string
): ISizeInputDto[] | undefined {
  return entriesOf(raw, key, MENU_OP_LIMITS.sizes, sizeOf);
}

/** The fields of one schedule row (plan P4-2). */
const HOUR_RANGE_FIELDS: ReadonlyArray<keyof IHourRangeDto> = [
  'day',
  'from',
  'to',
];

/** An `HH:MM` time; undefined when absent. */
function hourOf(raw: Record<string, unknown>, key: string): string | undefined {
  const value = getOptionalString(raw, key);
  if (value === null) return undefined;
  if (!HOUR_PATTERN.test(value))
    throw invalid(key, value, { pattern: HOUR_PATTERN.source });
  return value;
}

/** One schedule row: a day (0 = Sunday … 6) and two `HH:MM` times, all required. */
function hourRangeOf(raw: Record<string, unknown>): IHourRangeDto {
  const unknown = Object.keys(raw).find(
    (key) => !HOUR_RANGE_FIELDS.some((field) => field === key)
  );
  if (unknown !== undefined) throw invalid(unknown, raw[unknown]);
  const day = numberIn(raw, 'day', 0, SCHEDULE_DAY_MAX);
  if (day === undefined || !Number.isInteger(day))
    throw invalid('day', raw.day, { min: 0, max: SCHEDULE_DAY_MAX });
  const from = hourOf(raw, 'from');
  if (from === undefined) throw invalid('from', raw.from);
  const to = hourOf(raw, 'to');
  if (to === undefined) throw invalid('to', raw.to);
  return { day, from, to };
}

/**
 * The schedule of a change (plan P4-2): up to 50 rows, each checked (an
 * error names the row's field, e.g. «hourRanges[2].from»); null and an empty
 * list both mean «always» and are sent as null; undefined when absent.
 * Whether each row ends after it starts is the API's rule.
 */
function hourRangesOf(
  raw: Record<string, unknown>,
  key: string
): IHourRangeDto[] | null | undefined {
  const value = raw[key];
  if (value === null || (Array.isArray(value) && value.length === 0))
    return null;
  return entriesOf(raw, key, MENU_OP_LIMITS.hourRanges, hourRangeOf);
}

/**
 * How many decimals `value` is written with, as the Action API counts them
 * (class-validator's `maxDecimalPlaces`); a number written with an exponent
 * has more than any quantity may have.
 */
function decimalsOf(value: number): number {
  if (Number.isInteger(value)) return 0;
  const [, fraction] = String(value).split('.');
  return fraction === undefined || fraction.includes('e')
    ? Infinity
    : fraction.length;
}

/**
 * A stock quantity (plan P5-0): at least 0, or above 0 when `positive` (a
 * recipe line), at most `MENU_OP_LIMITS.stockQuantity`, with at most 3
 * decimals; undefined when absent.
 */
function stockQuantityOf(
  raw: Record<string, unknown>,
  key: string,
  positive = false
): number | undefined {
  const value = getOptionalNumber(raw, key);
  if (value === null) return undefined;
  if (
    !Number.isFinite(value) ||
    value < 0 ||
    (positive && value === 0) ||
    value > MENU_OP_LIMITS.stockQuantity ||
    decimalsOf(value) > STOCK_DECIMALS
  )
    throw invalid(key, value, {
      ...(positive ? { exclusiveMinimum: 0 } : { minimum: 0 }),
      maximum: MENU_OP_LIMITS.stockQuantity,
      maxDecimalPlaces: STOCK_DECIMALS,
    });
  return value;
}

/** The fields of one `items` entry of recordStock (plan P5-1). */
const STOCK_QUANTITY_FIELDS: ReadonlyArray<keyof IStockQuantityDto> = [
  'stockItemId',
  'quantity',
];

/**
 * One stock item and its quantity: both required (purchase/loss = the
 * amount moved, count = the counted total).
 */
function stockQuantityEntryOf(raw: Record<string, unknown>): IStockQuantityDto {
  const unknown = Object.keys(raw).find(
    (key) => !STOCK_QUANTITY_FIELDS.some((field) => field === key)
  );
  if (unknown !== undefined) throw invalid(unknown, raw[unknown]);
  const stockItemId = idOf(raw, 'stockItemId');
  if (stockItemId === undefined) throw invalid('stockItemId', raw.stockItemId);
  const quantity = stockQuantityOf(raw, 'quantity');
  if (quantity === undefined) throw invalid('quantity', raw.quantity);
  return { stockItemId, quantity };
}

/**
 * Up to 100 stock items of one movement, each checked (an error names the
 * entry's field, e.g. «items[2].quantity»); undefined when absent or empty.
 * A stock item named twice is the API's rule (DUPLICATE_TARGET).
 */
function stockQuantitiesOf(
  raw: Record<string, unknown>,
  key: string
): IStockQuantityDto[] | undefined {
  return entriesOf(raw, key, MENU_OP_LIMITS.stockItems, stockQuantityEntryOf);
}

/** The fields of one recipe line (plan P5-1). */
const RECIPE_LINE_FIELDS: ReadonlyArray<keyof IRecipeLineDto> = [
  'stockItemId',
  'stockItemRef',
  'quantity',
];

/**
 * One recipe line: a stock item (its id, or the ref of one the call creates)
 * and a quantity above 0. Which of the two names it is the API's rule.
 */
function recipeLineOf(raw: Record<string, unknown>): IRecipeLineDto {
  const unknown = Object.keys(raw).find(
    (key) => !RECIPE_LINE_FIELDS.some((field) => field === key)
  );
  if (unknown !== undefined) throw invalid(unknown, raw[unknown]);
  const quantity = stockQuantityOf(raw, 'quantity', true);
  if (quantity === undefined) throw invalid('quantity', raw.quantity);
  const line: IRecipeLineDto = { quantity };
  const stockItemId = idOf(raw, 'stockItemId');
  if (stockItemId !== undefined) line.stockItemId = stockItemId;
  const stockItemRef = refOf(raw, 'stockItemRef');
  if (stockItemRef !== undefined) line.stockItemRef = stockItemRef;
  return line;
}

/**
 * A recipe's lines (plan P5-1): up to 30, each checked (an error names the
 * line's field, e.g. «lines[1].quantity»). A sent empty list is kept: it
 * removes the recipe, so it must never read as absent; undefined when
 * absent.
 */
function recipeLinesOf(
  raw: Record<string, unknown>,
  key: string
): IRecipeLineDto[] | undefined {
  const value = raw[key];
  if (Array.isArray(value) && value.length === 0) return [];
  return entriesOf(raw, key, MENU_OP_LIMITS.recipeLines, recipeLineOf);
}

/**
 * Reads one field of a change: its value, or undefined when it was not sent.
 * A null is not sent, except where null is a value (`discountedPrice`,
 * `menuId`, `min`, `max`, `hourRanges`).
 */
type TFieldParsers = {
  readonly [K in TOpField]: (
    raw: Record<string, unknown>
  ) => IAssistantOpDto[K] | undefined;
};

/**
 * How each field is read (plan F10): types and ranges are checked here; a
 * null (unless it is a value of the field), an empty string or an empty list
 * (unless it is a value: `hourRanges`, `sizeIds`, `lines`) is absent.
 */
export const FIELD_PARSERS: TFieldParsers = {
  itemIds: (raw) => idsOf(raw, 'itemIds', MENU_OP_LIMITS.itemIds),
  categoryIds: (raw) => idsOf(raw, 'categoryIds', MENU_OP_LIMITS.categoryIds),
  all: (raw) => (getOptionalBoolean(raw, 'all') === true ? true : undefined),
  itemId: (raw) => idOf(raw, 'itemId'),
  sizeId: (raw) => idOf(raw, 'sizeId'),
  categoryId: (raw) => idOf(raw, 'categoryId'),
  available: (raw) => getOptionalBoolean(raw, 'available') ?? undefined,
  branchScope: (raw) =>
    getOptionalEnum(raw, 'branchScope', BRANCH_SCOPES) ?? undefined,
  price: (raw) => numberIn(raw, 'price', 0, MENU_OP_LIMITS.price),
  percent: (raw) =>
    numberIn(
      raw,
      'percent',
      MENU_OP_LIMITS.percentMin,
      MENU_OP_LIMITS.percentMax
    ),
  amount: (raw) =>
    numberIn(raw, 'amount', -MENU_OP_LIMITS.amount, MENU_OP_LIMITS.amount),
  roundTo: (raw) => roundStepOf(raw, 'roundTo'),
  name: (raw) => textOf(raw, 'name', 1, MENU_OP_LIMITS.name),
  description: (raw) =>
    textOf(raw, 'description', 0, MENU_OP_LIMITS.description),
  // Which ops take which mode ('scale' only with a percent or an amount) is
  // the API's rule.
  discountMode: (raw) =>
    getOptionalEnum(raw, 'discountMode', DISCOUNT_MODES) ?? undefined,
  discountedPrice: (raw) => nullableMoneyOf(raw, 'discountedPrice'),
  prices: (raw) => priceEntriesOf(raw, 'prices'),
  ref: (raw) => refOf(raw, 'ref'),
  categoryRef: (raw) => refOf(raw, 'categoryRef'),
  menuId: (raw) => nullableIdOf(raw, 'menuId'),
  posOnly: (raw) => getOptionalBoolean(raw, 'posOnly') ?? undefined,
  placement: (raw) =>
    getOptionalEnum(raw, 'placement', PLACEMENTS) ?? undefined,
  optionGroupId: (raw) => idOf(raw, 'optionGroupId'),
  optionGroupIds: (raw) =>
    idsOf(raw, 'optionGroupIds', MENU_OP_LIMITS.groupIds),
  choiceId: (raw) => idOf(raw, 'choiceId'),
  choiceIds: (raw) => idsOf(raw, 'choiceIds', MENU_OP_LIMITS.choiceIds),
  template: (raw) =>
    getOptionalEnum(raw, 'template', GROUP_TEMPLATES) ?? undefined,
  required: (raw) => getOptionalBoolean(raw, 'required') ?? undefined,
  multiple: (raw) => getOptionalBoolean(raw, 'multiple') ?? undefined,
  min: (raw) => nullableCountOf(raw, 'min'),
  max: (raw) => nullableCountOf(raw, 'max'),
  choices: (raw) => choicesOf(raw, 'choices'),
  copyOf: (raw) => idOf(raw, 'copyOf'),
  sizeIds: (raw) => sizeIdsOf(raw, 'sizeIds'),
  sizes: (raw) => sizesOf(raw, 'sizes'),
  hourRanges: (raw) => hourRangesOf(raw, 'hourRanges'),
  // '' clears the notes.
  notes: (raw) => textOf(raw, 'notes', 0, MENU_OP_LIMITS.notes),
  stockItemId: (raw) => idOf(raw, 'stockItemId'),
  movement: (raw) =>
    getOptionalEnum(raw, 'movement', STOCK_MOVEMENTS) ?? undefined,
  items: (raw) => stockQuantitiesOf(raw, 'items'),
  // A movement without a note: '' is no note.
  note: (raw) => textOf(raw, 'note', 0, MENU_OP_LIMITS.stockNote) || undefined,
  unit: (raw) => getOptionalEnum(raw, 'unit', STOCK_UNITS) ?? undefined,
  stockType: (raw) =>
    getOptionalEnum(raw, 'stockType', STOCK_TYPES) ?? undefined,
  cost: (raw) => numberIn(raw, 'cost', 0, MENU_OP_LIMITS.price),
  minimumQuantity: (raw) => stockQuantityOf(raw, 'minimumQuantity'),
  quantity: (raw) => stockQuantityOf(raw, 'quantity'),
  lines: (raw) => recipeLinesOf(raw, 'lines'),
};

/** Reads `field` of a change into `sent` when it was sent. */
function readField<K extends TOpField>(
  raw: Record<string, unknown>,
  field: K,
  sent: IAssistantOpDto
): void {
  const value = FIELD_PARSERS[field](raw);
  if (value !== undefined) sent[field] = value;
}

/** Copies `field` of what was sent into the op, when it was sent. */
function copyField<K extends TOpField>(
  sent: IAssistantOpDto,
  field: K,
  dto: IAssistantOpDto
): void {
  if (sent[field] !== undefined) dto[field] = sent[field];
}

/**
 * One change as the model sent it → the Action API op. Every field sent is
 * checked, whether or not the op takes it (the tool's schema offers them
 * all); the op keeps only its own fields. An unknown op lists only the ops
 * offered to the member (`offeredOps`, plan F10 naming rule).
 */
function opOf(
  raw: unknown,
  toolOps: readonly TAssistantOpName[],
  offeredOps: readonly TAssistantOpName[],
  toolFields: readonly TOpField[]
): IAssistantOpDto {
  if (!isRecord(raw)) throw invalid('change', raw);
  const unknown = Object.keys(raw).filter(
    (key) => key !== 'op' && !toolFields.some((field) => field === key)
  );
  if (unknown.length) throw invalid(unknown[0], raw[unknown[0]]);

  const name = getOptionalString(raw, 'op');
  const op = toolOps.find((candidate) => candidate === name);
  if (op === undefined)
    throw invalid('op', name ? name : raw.op, { allowed: offeredOps });

  const sent: IAssistantOpDto = { op };
  for (const field of toolFields) readField(raw, field, sent);
  const dto: IAssistantOpDto = { op };
  for (const field of fieldsOf([op])) copyField(sent, field, dto);
  return dto;
}

/**
 * The value of a field `OFFERED_ONLY_WITH` gates that changes nothing: what
 * the Action API does without the field (a product created visible, without
 * a discounted price).
 */
const NO_EFFECT_VALUES: Readonly<
  Partial<
    Record<TAssistantOpName, Readonly<Partial<Record<TOpField, unknown>>>>
  >
> = {
  createItem: { available: true, discountedPrice: null },
};

/**
 * What the model reads when a field is not offered to this member (W16b
 * A4): sent again without it, the change would not be what the user asked
 * (a product asked hidden would be created visible), so it tells the user
 * instead. Names no op (plan F10).
 */
const UNOFFERED_FIELD_NOTE =
  'this option is not available to this user here; tell the user, and do not send the change without it unless they ask';

/** `dto[field]` is the value that changes nothing (`NO_EFFECT_VALUES`). */
function hasNoEffect(dto: IAssistantOpDto, field: TOpField): boolean {
  const values = NO_EFFECT_VALUES[dto.op] ?? {};
  return (
    Object.prototype.hasOwnProperty.call(values, field) &&
    values[field] === dto[field]
  );
}

/**
 * The op with only the fields offered to this member. A field the op takes
 * only with an op this member is not offered (W13 decision: createItem's
 * Phase 2 fields during their dark launch, or with createCategory switched
 * off) is dropped when its value changes nothing, else INVALID_ARGS: never
 * dropped, so a product asked hidden or discounted is never created
 * otherwise.
 */
function withOfferedFields(
  dto: IAssistantOpDto,
  offeredOps: readonly TAssistantOpName[]
): IAssistantOpDto {
  const offered = offeredFieldsOf(dto.op, offeredOps);
  const gated = OP_FIELDS[dto.op].filter(
    (field) => dto[field] !== undefined && !offered.includes(field)
  );
  const refused = gated.find((field) => !hasNoEffect(dto, field));
  if (refused !== undefined)
    throw new HostToolError(
      'INVALID_ARGS',
      `Invalid "${refused}": ${UNOFFERED_FIELD_NOTE}`,
      { key: refused, value: dto[refused] }
    );
  const op: IAssistantOpDto = { op: dto.op };
  for (const field of fieldsOf([dto.op]))
    if (!gated.includes(field)) copyField(dto, field, op);
  return op;
}

/** Runs `read` on change `index`; its INVALID_ARGS names the change. */
function atChange<T>(index: number, read: () => T): T {
  try {
    return read();
  } catch (error) {
    if (!(error instanceof HostToolError)) throw error;
    throw new HostToolError(error.code, `changes[${index}]: ${error.message}`, {
      index,
      ...(isRecord(error.details) ? error.details : {}),
    });
  }
}

/**
 * `changes` of a proposal tool → 1–20 Action API ops of `toolOps` (plan
 * F10): a key no op of the tool takes is INVALID_ARGS, naming the change
 * (`index`) and the key; fields the op does not take are dropped, except a
 * field it takes only with an op the member is not offered (INVALID_ARGS).
 * Every op of `toolOps` is read, also one switched off for the member (a
 * stale schema gets its lock card, before any field check); an unknown op's
 * INVALID_ARGS lists only `offeredOps`, the ops this member is offered.
 */
export function opsFromArgs(
  args: Record<string, unknown>,
  toolOps: readonly TAssistantOpName[],
  offeredOps: readonly TAssistantOpName[]
): IAssistantOpDto[] {
  const changes = args.changes;
  if (
    !Array.isArray(changes) ||
    changes.length === 0 ||
    changes.length > MENU_OP_LIMITS.changes
  )
    throw invalid('changes', changes, { min: 1, max: MENU_OP_LIMITS.changes });
  const toolFields = fieldsOf(toolOps);
  const ops = changes.map((change, index) =>
    atChange(index, () => opOf(change, toolOps, offeredOps, toolFields))
  );
  // A change of an op the member is not offered answers with that op's lock
  // card (the tool stops before calling the Action API).
  if (!ops.every((dto) => offeredOps.includes(dto.op))) return ops;
  return ops.map((dto, index) =>
    atChange(index, () => withOfferedFields(dto, offeredOps))
  );
}
